import { Button } from "@/components/button";
import CalendarDateRangePicker from "@/components/data-range-picker";
import { Label } from "@/components/label";
import QuestionMarkTooltip from "@/components/questionmark-tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Separator } from "@/components/separator";

import EfficiencyTables from "./efficiency-tables";

import { useAudienceAndCampaignOptions, useBiddingEfficiency } from "@/http/api-hooks";
import { CampaignType, CampaignTypeValuesWithoutAll } from "@/http/api-types";
import { useUrlParams } from "@/http/use-url-params";
import { campaignTypeLabels } from "@/lib/mappings";
import { useFilterTracking } from "@/lib/mixpanel";
import { formatDateToYYYYMMDD, getAudienceSelectOptions, getCampaignSelectOptions } from "@/lib/utils";
import { addDays } from "date-fns";

function Efficiency() {
  const { paramState, updateParam, updateDateRange } = useUrlParams({
    audience: {
      name: "audience",
      type: "string",
      defaultValue: "",
    },
    campaign: {
      name: "campaign",
      type: "string",
      defaultValue: "",
    },
    campaignType: {
      name: "campaign_type",
      type: "enum",
      validValues: CampaignTypeValuesWithoutAll as readonly string[],
      defaultValue: "install" as CampaignType,
    },
    fromDate: {
      name: "from",
      type: "date",
      defaultValue: addDays(new Date(), -14),
    },
    toDate: {
      name: "to",
      type: "date",
      defaultValue: new Date(),
    },
  });

  const currentDate = {
    from: paramState.fromDate as Date,
    to: paramState.toDate as Date,
  };

  const { trackFilterChange } = useFilterTracking();

  const { data: audienceCampaignData, isLoading: audienceOptionsLoading } = useAudienceAndCampaignOptions({
    campaign_type: paramState.campaignType as string,
  });

  const audienceOptions = getAudienceSelectOptions(audienceCampaignData);
  const onAudienceSelectChange = (value: string) => {
    trackFilterChange("audience", value);
    updateParam("campaign", "");
    updateParam("audience", value);
  };

  const onCampaignSelectChange = (value: string) => {
    trackFilterChange("campaign", value);
    updateParam("campaign", value);
  };

  const onCampaignTypeChange = (value: string) => {
    trackFilterChange("campaign_type", value);
    updateParam("audience", "");
    updateParam("campaign", "");
    updateParam("campaignType", value);
  };

  const campaignOptions = getCampaignSelectOptions(audienceOptions, paramState.audience as string);

  const { trigger, isMutating, data, error } = useBiddingEfficiency({
    start_date: currentDate?.from ? formatDateToYYYYMMDD(currentDate.from) : undefined,
    end_date: currentDate?.to ? formatDateToYYYYMMDD(currentDate.to) : undefined,
    audience: parseInt(paramState.audience as string),
    campaign: parseInt(paramState.campaign as string),
    campaign_type: paramState.campaignType as string,
  });

  const onRunAnalysis = () => {
    trigger();
  };

  return (
    <>
      <div className="flex justify-start mb-8 space-x-8 items-end">
        <div className="grid grid-cols-[repeat(5,260px)] gap-4">
          <div>
            <Label className="font-semibold">Date</Label>
            <CalendarDateRangePicker
              date={currentDate}
              setDate={newDate => {
                if (newDate) {
                  trackFilterChange("date_range", { from: newDate.from, to: newDate.to });
                }
                updateDateRange(newDate, "fromDate", "toDate");
              }}
            />
          </div>
          <div>
            <Label className="font-semibold">Audience</Label>

            <Select
              value={paramState.audience as string}
              onValueChange={onAudienceSelectChange}
              disabled={audienceOptionsLoading}
            >
              <SelectTrigger value={paramState.audience as string} onReset={() => updateParam("audience", "")}>
                <SelectValue placeholder="Select audiences" />
              </SelectTrigger>
              <SelectContent>
                {audienceOptions &&
                  Object.keys(audienceOptions).map(option => (
                    <SelectItem key={option} value={option}>
                      {option + " - " + audienceOptions[option].audience_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-semibold">
              Campaign <QuestionMarkTooltip content="Select a specific campaign or leave empty for all campaigns." />
            </Label>

            <Select
              value={paramState.campaign as string}
              onValueChange={onCampaignSelectChange}
              disabled={audienceOptionsLoading}
            >
              <SelectTrigger value={paramState.campaign as string} onReset={() => updateParam("campaign", "")}>
                <SelectValue placeholder="Select campaigns" />
              </SelectTrigger>
              <SelectContent>
                {audienceOptions &&
                  campaignOptions.map(option => (
                    <SelectItem key={option.id} value={option.id.toString()}>
                      {option.id + " - " + option.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-semibold">Type</Label>
            <Select value={paramState.campaignType as string} onValueChange={onCampaignTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CampaignTypeValuesWithoutAll.map(value => (
                  <SelectItem key={value} value={value}>
                    {campaignTypeLabels[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end align-baseline">
            <Button onClick={onRunAnalysis}>Run Analysis</Button>
          </div>
        </div>
      </div>
      <Separator className="my-12" />
      <div className="grid gap-4">
        {/* you can replace data with demoData for development */}
        <EfficiencyTables data={data} error={error} isMutating={isMutating} />
      </div>
    </>
  );
}

export default Efficiency;
